import { Upload, X } from "lucide-react";
import { type DragEvent, useId, useState } from "react";
import { useTranslation } from "react-i18next";

export type FileDropZoneProps = {
  /** 欄の名前（「BPMN ファイル」など）。入力のラベルになる。 */
  readonly label: string;
  /**
   * ファイル選択の画面で絞る拡張子（".bpmn" など）。ドロップには効かないので、
   * 使えるかの判定は呼び出し側が拡張子を見て行う。
   */
  readonly accept: string;
  readonly multiple?: boolean;
  readonly hint: string;
  /** 枠の中に出す選んだファイルの名前。null なら選び方の案内を出す。 */
  readonly selectedName: string | null;
  /** 使えない理由。無ければ null。 */
  readonly issue: string | null;
  readonly onFiles: (_files: readonly File[]) => void;
  /** 選んだものを外す操作。選んだ名前があり、これを渡したときだけ × を出す。 */
  readonly onClear?: () => void;
  /** 選べない間（送信中など）。選択もドロップも受け付けない。 */
  readonly disabled?: boolean;
};

/**
 * ファイルを選ぶ欄。クリックで選ぶほか、ドロップもできる。
 *
 * 見た目は GAIA のスキル登録（点線の 1 行、選んだ後はファイル名と ×）にそろえる。
 * 作りは input を label で包む形にする。ボタンから非表示の input を開く作りだと、
 * キーボード操作と読み上げを自前で足す必要があるが、この形ならブラウザ標準のまま効く。
 *
 * 何を選べるか（.bpmn 1 つ / .md 1 つ / .json 複数）と、選んだものの読み取り・判定は
 * 呼び出し側が持つ。この部品は選ばれたファイルを渡し、渡された名前と理由を出すだけ。
 */
export function FileDropZone({
  label,
  accept,
  multiple = false,
  hint,
  selectedName,
  issue,
  onFiles,
  onClear,
  disabled = false,
}: FileDropZoneProps) {
  const { t } = useTranslation("common");
  const id = useId();
  const labelId = `${id}-label`;
  const nameId = `${id}-name`;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const [isDragging, setIsDragging] = useState(false);

  // input の value は選ぶたびに空に戻すので、何を選んだかは説明として読ませる
  const describedBy = [
    hintId,
    selectedName === null ? null : nameId,
    issue === null ? null : errorId,
  ]
    .filter((part) => part !== null)
    .join(" ");

  // 1 つしか選べない欄に複数ドロップされたら最初の 1 つだけ渡す（accept と同じく
  // multiple もドロップには効かない）
  const pick = (files: FileList) => {
    const picked = multiple ? Array.from(files) : Array.from(files).slice(0, 1);
    if (picked.length > 0) onFiles(picked);
  };

  const onDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!disabled) setIsDragging(true);
  };
  // 枠の中の子要素へ移っただけでも dragleave が出るので、枠の外へ出たときだけ戻す
  const onDragLeave = (event: DragEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setIsDragging(false);
    }
  };
  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (!disabled) pick(event.dataTransfer.files);
  };

  return (
    <div className="flex flex-col gap-1 text-sm">
      <span id={labelId} className="font-medium">
        {label}
      </span>
      {/* 外すボタンは label の外に置く（label の中に操作部品を入れ子にしない） */}
      <div
        data-testid="file-drop-zone"
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`focus-within:ring-ring flex w-full items-center rounded-md border transition-colors focus-within:ring-2 motion-reduce:transition-none ${
          isDragging
            ? "border-primary bg-accent border-dashed"
            : selectedName === null
              ? "border-muted-foreground/40 hover:border-primary hover:bg-accent border-dashed"
              : "border-border bg-muted"
        }`}
      >
        <label
          className={`flex min-w-0 flex-1 items-center gap-3 px-3 py-3 ${
            disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
          }`}
        >
          <input
            type="file"
            accept={accept}
            multiple={multiple}
            disabled={disabled}
            className="sr-only"
            aria-labelledby={labelId}
            aria-describedby={describedBy}
            onChange={(event) => {
              const files = event.target.files;
              if (files !== null) pick(files);
              // 同じファイルを選び直しても change が出るように空に戻す
              event.target.value = "";
            }}
          />
          <Upload
            aria-hidden="true"
            className="text-muted-foreground size-4 shrink-0"
          />
          <span
            id={nameId}
            className={`min-w-0 flex-1 truncate ${selectedName === null ? "text-muted-foreground" : ""}`}
          >
            {selectedName === null
              ? t("processDesign.fileDrop.placeholder")
              : selectedName}
          </span>
        </label>
        {selectedName === null || onClear === undefined ? null : (
          <button
            type="button"
            aria-label={t("processDesign.fileDrop.clear")}
            className="text-muted-foreground hover:text-foreground hover:bg-background mr-2 rounded-sm p-1"
            onClick={onClear}
          >
            <X aria-hidden="true" className="size-4" />
          </button>
        )}
      </div>
      <p id={hintId} className="text-muted-foreground m-0 text-xs">
        {hint}
      </p>
      {issue === null ? null : (
        <p id={errorId} role="alert" className="text-destructive m-0 text-xs">
          {issue}
        </p>
      )}
    </div>
  );
}
